import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@shared/utils'
import { useChangePassword } from '@modules/auth'

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Vui lòng nhập mật khẩu hiện tại'),
    newPassword: z.string().min(6, 'Mật khẩu mới ít nhất 6 ký tự'),
    confirmPassword: z.string().min(1, 'Vui lòng xác nhận mật khẩu'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Mật khẩu xác nhận không khớp',
    path: ['confirmPassword'],
  })

type PasswordFormValues = z.infer<typeof passwordSchema>

// ── PasswordField ─────────────────────────────────────────────────────────────
function PasswordField({
  id,
  label,
  error,
  registration,
}: {
  id: string
  label: string
  error?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registration: any
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground" htmlFor={id}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete="off"
          className={cn(
            'h-8 w-full rounded-lg border border-border bg-background px-2 pr-8 text-sm text-foreground',
            'placeholder:text-muted-foreground focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
            error && 'border-destructive focus:border-destructive focus:ring-destructive/20',
          )}
          {...registration}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-muted-foreground"
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
      {error && <p className="text-xs text-state-critical">{error}</p>}
    </div>
  )
}

// ── Đổi mật khẩu ──
/** Cài đặt › Tài khoản — đổi mật khẩu */
export function PasswordPanel() {
  const { mutateAsync: changePassword, isPending: changingPassword } = useChangePassword()

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  })

  const handleSubmit = async (values: PasswordFormValues) => {
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      })
      form.reset()
    } catch {
      // error handled by mutation onError (toast)
    }
  }

  return (
    <div className="max-w-md">
      <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
        <h2 className="mb-1 text-sm font-semibold text-foreground">Đổi mật khẩu</h2>
        <p className="mb-4 text-xs text-muted-foreground">Mật khẩu mới phải có ít nhất 6 ký tự</p>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
          <PasswordField
            id="currentPassword"
            label="Mật khẩu hiện tại"
            error={form.formState.errors.currentPassword?.message}
            registration={form.register('currentPassword')}
          />
          <PasswordField
            id="newPassword"
            label="Mật khẩu mới"
            error={form.formState.errors.newPassword?.message}
            registration={form.register('newPassword')}
          />
          <PasswordField
            id="confirmPassword"
            label="Xác nhận mật khẩu mới"
            error={form.formState.errors.confirmPassword?.message}
            registration={form.register('confirmPassword')}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={changingPassword}
              className="h-8 rounded-lg bg-brand-500 px-6 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {changingPassword ? 'Đang lưu...' : 'Đổi mật khẩu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
